/**
 * Debug — the AI's diagnostic view. Gated behind `ctx.config.debug` and off
 * until a shot (or `ctx.ai.setDebug`) turns it on, so it can never leak into
 * another team's screenshot.
 *
 * Draws: the baked navmesh coloured by cover class, the direction each cover
 * point protects from, off-mesh links, live paths, perception cones, awareness
 * meters and the behaviour-tree node above each agent.
 *
 * owner: ai
 */
import * as THREE from 'three';
import { COVER_DIRS, LINK_VAULT } from './Nav.js';

/**
 * Values deliberately above 1: the post pipeline tonemaps and depth-fogs the
 * whole frame, and a debug overlay authored at display values comes out as milk
 * at twenty metres. These are chosen to survive that.
 */
const COL_WALK = new THREE.Color(0.06, 0.42, 1.55);
const COL_LOW = new THREE.Color(2.20, 1.05, 0.06);
const COL_HIGH = new THREE.Color(0.10, 2.10, 0.70);
const COL_PATH = new THREE.Color(0.35, 2.40, 0.55);
const COL_FLANK = new THREE.Color(2.60, 0.30, 1.90);
const COL_CONE = new THREE.Color(0.40, 1.30, 2.40);
const COL_LINK = new THREE.Color(2.40, 0.30, 2.40);   // vault
const COL_DROP = new THREE.Color(1.60, 0.55, 0.10);   // ledge drop

const MAX_DYN_VERTS = 24000;

export class AIDebug {
  constructor(ctx) {
    this.ctx = ctx;
    this.group = new THREE.Group();
    this.group.name = 'ai-debug';
    this.group.visible = false;
    this.group.renderOrder = 900;
    ctx.scene.add(this.group);

    this.showNav = true;
    this.showCover = true;
    this.showLinks = true;
    this.showPaths = true;
    this.showPerception = true;
    this.showLabels = true;

    this.navMesh = null;
    this.coverTicks = null;
    this.linkLines = null;

    // dynamic line pool
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(MAX_DYN_VERTS * 3), 3));
    g.setAttribute('color', new THREE.BufferAttribute(new Float32Array(MAX_DYN_VERTS * 3), 3));
    g.setDrawRange(0, 0);
    this.dynGeo = g;
    this.dynLines = new THREE.LineSegments(g, new THREE.LineBasicMaterial({
      vertexColors: true, fog: false, depthTest: false, depthWrite: false,
      transparent: true, opacity: 1.0, toneMapped: false,
    }));
    this.dynLines.frustumCulled = false;
    this.dynLines.renderOrder = 905;
    this.group.add(this.dynLines);
    this._dynCount = 0;

    this.labels = [];
    this.labelCache = new Map();
    this.labelGroup = new THREE.Group();
    this.group.add(this.labelGroup);
  }

  setEnabled(on) {
    this.group.visible = !!on;
    return this.group.visible;
  }
  get enabled() { return this.group.visible; }

  /* ---------------------------------------------------------------- *
   * static: the baked navmesh + cover map
   * ---------------------------------------------------------------- */
  buildNav(nav) {
    this.clearNav();
    if (!nav || !nav.ready) return;
    const n = nav.count;
    const half = nav.cell * 0.42;

    const pos = new Float32Array(n * 6 * 3);
    const col = new Float32Array(n * 6 * 3);
    const c = new THREE.Color();
    let w = 0;
    for (let i = 0; i < n; i++) {
      const x = nav.px[i], y = nav.py[i] + 0.035, z = nav.pz[i];
      if (nav.coverHigh[i]) c.copy(COL_HIGH);
      else if (nav.coverLow[i]) c.copy(COL_LOW);
      else c.copy(COL_WALK);
      const quad = [
        [-half, -half], [half, -half], [half, half],
        [-half, -half], [half, half], [-half, half],
      ];
      for (const [dx, dz] of quad) {
        pos[w * 3] = x + dx; pos[w * 3 + 1] = y; pos[w * 3 + 2] = z + dz;
        col[w * 3] = c.r; col[w * 3 + 1] = c.g; col[w * 3 + 2] = c.b;
        w++;
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    this.navMesh = new THREE.Mesh(g, new THREE.MeshBasicMaterial({
      vertexColors: true, transparent: true, opacity: 0.72, fog: false,
      side: THREE.DoubleSide, depthWrite: false, toneMapped: false,
    }));
    this.navMesh.frustumCulled = false;
    this.navMesh.renderOrder = 901;
    this.group.add(this.navMesh);

    // cover direction ticks
    const ticks = [];
    const tcol = [];
    for (let i = 0; i < n; i++) {
      const lo = nav.coverLow[i], hi = nav.coverHigh[i];
      if (!lo && !hi) continue;
      const x = nav.px[i], y = nav.py[i] + 0.06, z = nav.pz[i];
      for (let d = 0; d < COVER_DIRS; d++) {
        const bit = 1 << d;
        const isHigh = (hi & bit) !== 0;
        if (!isHigh && !(lo & bit)) continue;
        const a = (d / COVER_DIRS) * Math.PI * 2;
        const dx = Math.sin(a) * 0.40, dz = Math.cos(a) * 0.40;
        ticks.push(x, y, z, x + dx, y + (isHigh ? 0.85 : 0.34), z + dz);
        const cc = isHigh ? COL_HIGH : COL_LOW;
        tcol.push(cc.r, cc.g, cc.b, cc.r, cc.g, cc.b);
      }
    }
    if (ticks.length) {
      const tg = new THREE.BufferGeometry();
      tg.setAttribute('position', new THREE.Float32BufferAttribute(ticks, 3));
      tg.setAttribute('color', new THREE.Float32BufferAttribute(tcol, 3));
      this.coverTicks = new THREE.LineSegments(tg, new THREE.LineBasicMaterial({
        vertexColors: true, fog: false, transparent: true, opacity: 1.0, toneMapped: false,
      }));
      this.coverTicks.frustumCulled = false;
      this.coverTicks.renderOrder = 902;
      this.group.add(this.coverTicks);
    }

    // off-mesh links — vaults and ledge drops read differently on purpose
    const links = [], lcol = [];
    for (let i = 0; i < n; i++) {
      for (let e = nav.adjStart[i]; e < nav.adjStart[i + 1]; e++) {
        const kind = nav.adjKind[e];
        if (kind === 0) continue;
        const j = nav.adjNode[e];
        links.push(nav.px[i], nav.py[i] + 0.45, nav.pz[i], nav.px[j], nav.py[j] + 0.45, nav.pz[j]);
        const cc = kind === LINK_VAULT ? COL_LINK : COL_DROP;
        lcol.push(cc.r, cc.g, cc.b, cc.r, cc.g, cc.b);
      }
    }
    if (links.length) {
      const lg = new THREE.BufferGeometry();
      lg.setAttribute('position', new THREE.Float32BufferAttribute(links, 3));
      lg.setAttribute('color', new THREE.Float32BufferAttribute(lcol, 3));
      this.linkLines = new THREE.LineSegments(lg, new THREE.LineBasicMaterial({
        vertexColors: true, fog: false, transparent: true, opacity: 0.85, toneMapped: false,
      }));
      this.linkLines.frustumCulled = false;
      this.linkLines.renderOrder = 903;
      this.group.add(this.linkLines);
    }
  }

  clearNav() {
    for (const m of [this.navMesh, this.coverTicks, this.linkLines]) {
      if (!m) continue;
      m.removeFromParent();
      m.geometry.dispose();
      m.material.dispose();
    }
    this.navMesh = this.coverTicks = this.linkLines = null;
  }

  /* ---------------------------------------------------------------- *
   * dynamic
   * ---------------------------------------------------------------- */
  _line(ax, ay, az, bx, by, bz, c) {
    if (this._dynCount + 2 > MAX_DYN_VERTS) return;
    const p = this.dynGeo.attributes.position.array;
    const k = this.dynGeo.attributes.color.array;
    let i = this._dynCount * 3;
    p[i] = ax; p[i + 1] = ay; p[i + 2] = az;
    k[i] = c.r; k[i + 1] = c.g; k[i + 2] = c.b;
    i += 3;
    p[i] = bx; p[i + 1] = by; p[i + 2] = bz;
    k[i] = c.r; k[i + 1] = c.g; k[i + 2] = c.b;
    this._dynCount += 2;
  }

  update(agents, squads, nav) {
    if (!this.group.visible) return;
    // The baked layers are heavy; a shot that is about one agent's decision
    // reads far better with them off.
    if (this.navMesh) this.navMesh.visible = this.showNav;
    if (this.coverTicks) this.coverTicks.visible = this.showNav && this.showCover;
    if (this.linkLines) this.linkLines.visible = this.showNav && this.showLinks;
    this._dynCount = 0;
    const tmp = new THREE.Color();

    if (this.showPaths) {
      for (const a of agents) {
        if (!a.alive || !a.path) continue;
        const isFlank = a.role === 'flanker';
        const c = isFlank ? COL_FLANK : COL_PATH;
        let px = a.pos.x, py = a.pos.y + 0.12, pz = a.pos.z;
        for (let i = a.pathIndex; i < a.path.length; i++) {
          const q = a.path[i];
          this._line(px, py, pz, q.x, q.y + 0.12, q.z, c);
          // a small upright pin at each corner
          this._line(q.x, q.y + 0.12, q.z, q.x, q.y + 0.45, q.z, c);
          px = q.x; py = q.y + 0.12; pz = q.z;
        }
      }
    }

    if (this.showPerception) {
      for (const a of agents) {
        if (!a.alive) continue;
        const eye = a.eyePos(new THREE.Vector3());
        const half = Math.acos(a.perception.fovCos);
        const range = Math.min(a.perception.maxRange, 16);
        tmp.copy(COL_CONE).lerp(new THREE.Color(1, 0.25, 0.15), a.perception.awareness);
        const steps = 7;
        let lx = 0, lz = 0;
        for (let s = 0; s <= steps; s++) {
          const t = -half + (2 * half) * (s / steps);
          const ang = a.aimYaw + t;
          const x = eye.x + Math.sin(ang) * range;
          const z = eye.z + Math.cos(ang) * range;
          if (s === 0 || s === steps) this._line(eye.x, eye.y, eye.z, x, eye.y, z, tmp);
          if (s > 0) this._line(lx, eye.y, lz, x, eye.y, z, tmp);
          lx = x; lz = z;
        }
        // awareness meter, a bar over the head
        const bx = a.pos.x, by = a.pos.y + a.height + 0.30, bz = a.pos.z;
        const W = 0.55;
        this._line(bx - W / 2, by, bz, bx + W / 2, by, bz, new THREE.Color(0.15, 0.15, 0.15));
        const fill = W * a.perception.awareness;
        if (fill > 0.001) {
          tmp.setRGB(1 - a.perception.awareness * 0.2, 0.9 - a.perception.awareness * 0.85, 0.15);
          this._line(bx - W / 2, by + 0.012, bz, bx - W / 2 + fill, by + 0.012, bz, tmp);
        }
        // line of sight
        if (a.perception.hasLOS) {
          const p = a.perception.losPoint;
          this._line(eye.x, eye.y, eye.z, p.x, p.y, p.z, new THREE.Color(1, 0.35, 0.2));
        }
      }
    }

    // flank assignment highlight
    for (const sq of squads) {
      if (sq.flankGoal == null || sq.flankGoal < 0 || !nav || !nav.ready) continue;
      const n = sq.flankGoal;
      const x = nav.px[n], y = nav.py[n], z = nav.pz[n];
      for (let d = 0; d < 8; d++) {
        const a0 = (d / 8) * Math.PI * 2, a1 = ((d + 1) / 8) * Math.PI * 2;
        this._line(
          x + Math.sin(a0) * 0.6, y + 0.08, z + Math.cos(a0) * 0.6,
          x + Math.sin(a1) * 0.6, y + 0.08, z + Math.cos(a1) * 0.6, COL_FLANK);
      }
      this._line(x, y + 0.08, z, x, y + 1.6, z, COL_FLANK);
      if (sq.flankAgent && sq.flankAgent.alive) {
        this._line(sq.flankAgent.pos.x, sq.flankAgent.pos.y + 1.6, sq.flankAgent.pos.z,
          x, y + 1.6, z, COL_FLANK);
      }
      // where the squad thinks the player is
      const p = sq.playerPos;
      this._line(p.x - 0.5, p.y + 0.05, p.z, p.x + 0.5, p.y + 0.05, p.z, new THREE.Color(1, 0.2, 0.2));
      this._line(p.x, p.y + 0.05, p.z - 0.5, p.x, p.y + 0.05, p.z + 0.5, new THREE.Color(1, 0.2, 0.2));
      this._line(p.x, p.y + 0.05, p.z, p.x, p.y + 1.8, p.z, new THREE.Color(1, 0.2, 0.2));
    }

    this.dynGeo.setDrawRange(0, this._dynCount);
    this.dynGeo.attributes.position.needsUpdate = true;
    this.dynGeo.attributes.color.needsUpdate = true;
    this.dynGeo.computeBoundingSphere();

    if (this.showLabels) this._updateLabels(agents);
  }

  _updateLabels(agents) {
    let i = 0;
    for (const a of agents) {
      if (!a.alive) continue;
      const text = `${a.behaviour.path}  [${a.role}]`;
      const sp = this.labels[i] || this._makeLabel(i);
      const tex = this._texture(text);
      if (sp.material.map !== tex) {
        sp.material.map = tex;
        sp.material.needsUpdate = true;
        sp.scale.set(tex.userData.w / 128, tex.userData.h / 128, 1);
      }
      sp.position.set(a.pos.x, a.pos.y + a.height + 0.55, a.pos.z);
      sp.visible = true;
      i++;
    }
    for (; i < this.labels.length; i++) this.labels[i].visible = false;
  }

  _makeLabel(i) {
    const sp = new THREE.Sprite(new THREE.SpriteMaterial({
      transparent: true, depthTest: false, fog: false, sizeAttenuation: true,
    }));
    sp.renderOrder = 910;
    this.labelGroup.add(sp);
    this.labels[i] = sp;
    return sp;
  }

  _texture(text) {
    let t = this.labelCache.get(text);
    if (t) return t;
    const pad = 10;
    const cv = document.createElement('canvas');
    const cx = cv.getContext('2d');
    cx.font = 'bold 30px monospace';
    const w = Math.ceil(cx.measureText(text).width) + pad * 2;
    cv.width = nextPow2(w); cv.height = 64;
    const c2 = cv.getContext('2d');
    c2.font = 'bold 30px monospace';
    c2.textBaseline = 'middle';
    c2.fillStyle = 'rgba(4,8,10,0.78)';
    roundRect(c2, 0, 8, w, 46, 8);
    c2.fill();
    c2.fillStyle = '#7dffb0';
    c2.fillText(text, pad, 32);
    t = new THREE.CanvasTexture(cv);
    t.colorSpace = THREE.SRGBColorSpace;
    t.userData = { w, h: 64 };
    this.labelCache.set(text, t);
    return t;
  }

  dispose() {
    this.clearNav();
    this.dynGeo.dispose();
    this.dynLines.material.dispose();
    for (const t of this.labelCache.values()) t.dispose();
    this.group.removeFromParent();
  }
}

function nextPow2(v) { let p = 1; while (p < v) p *= 2; return p; }
function roundRect(c, x, y, w, h, r) {
  c.beginPath();
  c.moveTo(x + r, y);
  c.arcTo(x + w, y, x + w, y + h, r);
  c.arcTo(x + w, y + h, x, y + h, r);
  c.arcTo(x, y + h, x, y, r);
  c.arcTo(x, y, x + w, y, r);
  c.closePath();
}
